import QRCode from "qrcode";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { showPrice } from "./commonFunctions";

/**
 * Thermal receipt template (80mm) -> downloadable PDF.
 *
 * Pipeline:  Order API + Customer-Invoice settings
 *              -> fixed 576px HTML receipt (English + Arabic)
 *              -> html2canvas -> PNG
 *              -> jsPDF (80mm-wide page) -> downloaded as a .pdf
 *
 * We render to a pixel image (not text) on purpose: Arabic shaping, the logo and
 * the QR code all render in the browser, so the PDF is pixel-identical to the
 * on-screen receipt regardless of fonts available to any PDF/text engine.
 *
 * 576px = the dot width of a standard 80mm thermal head, so the layout is locked
 * to 80mm paper width.
 */

// Standard 80mm print width in dots. Overridable via env for 58mm (384) etc.
export const RECEIPT_WIDTH_PX = Number(process.env.REACT_APP_RECEIPT_WIDTH_PX) || 576;

const CURRENCY = "SAR";

// Bilingual labels. English is the source of truth; Arabic mirrors the screenshot.
const AR = {
    orderId: "رقم الطلب",
    orderMode: "نوع الطلب",
    customer: "اسم العميل",
    createdBy: "أنشئ بواسطة",
    orderDate: "تاريخ الطلب",
    orderTime: "وقت الطلب",
    paymentMethod: "طريقة الدفع",
    paymentStatus: "حالة الدفع",
    outlet: "اسم الفرع",
    item: "الصنف",
    qty: "الكمية",
    price: "السعر",
    orderAmount: "مبلغ الطلب",
    discount: "الخصم",
    payable: "المبلغ المستحق",
    subtotal: "المجموع الفرعي",
    surcharge: "رسوم إضافية",
    taxes: "الضرائب",
    vat: "ضريبة القيمة المضافة",
    netAmount: "المبلغ الصافي",
    thankYou: "شكراً لك",
    scan: "امسح الرمز للفاتورة",
    vatNo: "الرقم الضريبي",
};

// Arabic (Cairo) font stack used wherever Arabic glyphs appear.
const AR_FONT = "'Cairo','Noto Sans Arabic',sans-serif";

const esc = (val) => {
    if (val === null || val === undefined) return "";
    return String(val)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
};

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "");

const money = (amount) => `${showPrice(amount)} ${CURRENCY}`;

/** Left-pad an order id to at least 4 digits, mirroring the order view screen. */
const formatOrderId = (orderId) => {
    if (orderId === null || orderId === undefined) return "";
    const str = String(orderId);
    return str.length >= 4 ? str : str.padStart(4, "0");
};

/**
 * A bilingual receipt row: English label (left) + Arabic label (right of the label
 * column), with the value right-aligned at the paper edge. `big` emphasises totals.
 */
const metaRow = (en, ar, value, { bold = false, big = false } = {}) => {
    if (value === "" || value === null || value === undefined) return "";
    const weight = bold ? "700" : "500";
    const size = big ? "font-size:23px;" : "";
    const border = big ? "border-top:2px solid #000;border-bottom:2px solid #000;" : "";
    return `
        <tr style="font-weight:${weight};${size}${border}">
            <td style="padding:4px 0;vertical-align:middle;width:62%">
                <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
                    <span>${esc(en)}</span>
                    <span dir="rtl" style="font-family:${AR_FONT}">${esc(ar)}</span>
                </div>
            </td>
            <td style="padding:4px 0 4px 8px;text-align:right;white-space:nowrap;vertical-align:middle;font-family:monospace">${esc(value)}</td>
        </tr>`;
};

const rule = () => `<div style="border-top:1px dashed #000;margin:8px 0"></div>`;

/** Build the receipt's inner HTML from an order + customer-invoice settings. */
const buildReceiptHtml = (order = {}, settings = {}, hasQr = false) => {
    const headingEn = settings?.heading_text || "Simplified Tax Invoice";
    const headingAr = settings?.heading_ar_text || "فاتورة ضريبية مبسطة";
    // VAT number resolved by the backend from the order's ZATCA configuration
    // (online/device/company-panel rule); fall back to the invoice settings.
    const vatNo = order?.invoice_vat_no || settings?.vat_no || order?.outlet?.vat_no || "";
    const logo = settings?.logo || "";
    const outletLine = cap(order?.outlet?.location || order?.outlet?.outlet_name || "");

    const orderIdVal = order?.order_id || `#${formatOrderId(order?.id)}`;
    const createdBy = order?.user
        ? `${cap(order.user.first_name)} ${cap(order.user.last_name)}`.trim()
        : "";
    const created = order?.created_at || order?.transaction?.created_at;
    const createdDate = created ? new Date(created) : null;
    const dateStr = createdDate && !isNaN(createdDate) ? createdDate.toLocaleDateString() : "";
    const timeStr = createdDate && !isNaN(createdDate) ? createdDate.toLocaleTimeString() : "";

    // ── Items (bilingual name where available) ────────────────────────────
    const itemRows = [];
    // Add-on price follows the item's tax type: inclusive shows price + tax, otherwise
    // the base price (mirrors the order view). Item/combo prices stay at base price.
    const priceByTaxType = (taxType, price, taxAmount) =>
        taxType === "inclusive" ? Number(price || 0) + Number(taxAmount || 0) : price;
    (order?.customer_order_menu_item_relations || []).forEach((it) => {
        const nameEn = it?.menu_item?.item_name || "Item";
        const nameAr = it?.menu_item?.item_name_ar || "";
        itemRows.push(itemRow(nameEn, nameAr, it?.quantity, it?.price));
        (it?.menuAddOns || []).forEach((ad) => {
            const adName = ad?.menu_add_on?.name || "Add on";
            const adNameAr = ad?.menu_add_on?.name_ar ? `+ ${ad.menu_add_on.name_ar}` : "";
            // Add-on qty and price are shown as-is (NOT scaled by the item quantity),
            // mirroring the order view.
            const adQty = Number(ad?.quantity || 0);
            const adPrice = priceByTaxType(it?.tax_type, ad?.price, ad?.tax_amount);
            // Indent add-ons so they nest visually under their menu item.
            itemRows.push(itemRow(`+ ${adName}`, adNameAr, adQty, adPrice, 20));
        });
    });
    // Meal combos: combo line, combo add-ons total, each combo menu item and its
    // add-ons — mirrors the order view (no quantity multiplication anywhere).
    (order?.customer_order_meal_combo_relations || []).forEach((c) => {
        const nameEn = c?.menu_meal_combo?.name || "Combo";
        const nameAr = c?.menu_meal_combo?.name_ar || "";
        itemRows.push(itemRow(nameEn, nameAr, c?.quantity, c?.price));

        (c?.comboMenuItems || []).forEach((item) => {
            // Indent the combo's menu items, and indent their add-ons further, so the
            // hierarchy under the combo is visible on the receipt.
            itemRows.push(itemRow(`- ${item?.item_name || "Item"}`, "", item?.quantity, "", 20));
            (item?.addons || []).forEach((ad) => {
                const adPrice = priceByTaxType(c?.tax_type, ad?.price, ad?.tax_amount);
                itemRows.push(itemRow(`+ ${ad?.addon_name || "Add on"}`, "", ad?.quantity, adPrice, 40));
            });
        });
    });

    const promoText = settings?.sub_invoice && settings?.sub_invoice_text ? settings.sub_invoice_text : "";

    return `
    <div style="width:${RECEIPT_WIDTH_PX}px;box-sizing:border-box;background:#fff;color:#000;
                padding:20px 18px;font-family:Arial,Helvetica,sans-serif;font-size:21px;line-height:1.4">

        <div style="text-align:center">
            ${logo ? `<img src="${esc(logo)}" alt="logo" crossorigin="anonymous"
                        style="max-width:300px;max-height:150px;object-fit:contain;margin-bottom:8px" />` : ""}
            <div style="font-size:34px;font-weight:700;letter-spacing:.5px">${esc(headingEn)}</div>
            <div dir="rtl" style="font-size:30px;font-weight:700;font-family:${AR_FONT};margin-top:2px">${esc(headingAr)}</div>
            ${vatNo ? `<div style="font-size:18px;margin-top:6px">VAT No / ${esc(AR.vatNo)}: ${esc(vatNo)}</div>` : ""}
            ${outletLine ? `<div style="font-size:19px;margin-top:2px">${esc(outletLine)}</div>` : ""}
        </div>

        ${rule()}

        <table style="width:100%;border-collapse:collapse;font-size:20px;table-layout:fixed">
            <tbody>
                ${metaRow("Order ID", AR.orderId, orderIdVal)}
                ${metaRow("Order Mode", AR.orderMode, cap(order?.order_mode_configuration?.name))}
                ${metaRow("Customer Name", AR.customer, cap(order?.customer_name))}
                ${metaRow("Created By", AR.createdBy, createdBy)}
                ${metaRow("Order Date", AR.orderDate, dateStr)}
                ${metaRow("Order Time", AR.orderTime, timeStr)}
                ${metaRow("Payment Method", AR.paymentMethod, cap(order?.transaction?.tender_configuration?.name || order?.transaction?.tender_name))}
                ${metaRow("Payment Status", AR.paymentStatus, cap(order?.transaction?.payment_status))}
                ${metaRow("Outlet Name", AR.outlet, cap(order?.outlet?.outlet_name))}
            </tbody>
        </table>

        ${rule()}

        <table style="width:100%;border-collapse:collapse;font-size:20px">
            <tbody>
                <tr style="font-weight:700">
                    <td style="padding:4px 0"></td>
                    <td style="padding:4px 0;text-align:center;white-space:nowrap">Qty</td>
                    <td style="padding:4px 0;text-align:right;white-space:nowrap;font-family:monospace">${CURRENCY}</td>
                </tr>
                ${itemRows.join("")}
            </tbody>
        </table>

        ${rule()}

        <table style="width:100%;border-collapse:collapse;font-size:20px;table-layout:fixed">
            <tbody>
                ${metaRow("Order Amount", AR.orderAmount, money(order?.order_amount))}
                ${metaRow("Discount", AR.discount, money(order?.discount_price))}
                ${metaRow("Payable Amount", AR.payable, money(order?.payable_amount))}
                ${metaRow("Subtotal", AR.subtotal, money(order?.sub_total_price))}
                ${metaRow("Surcharge", AR.surcharge, money(order?.surcharge_price))}
                ${metaRow("VAT (15%)", AR.vat, money(order?.tax_total_price))}
                ${metaRow("Net Amount", AR.netAmount, money(order?.net_amount ?? order?.total_price), { bold: true, big: true })}
            </tbody>
        </table>

        ${hasQr ? `
        <div style="text-align:center;margin-top:14px">
            <div style="display:inline-block;background:#ffffff;padding:16px;line-height:0">
                <div id="qr-slot" style="width:${QR_CSS_PX}px;height:${QR_CSS_PX}px;background:#ffffff"></div>
            </div>
            <div style="font-size:16px;margin-top:6px">ZATCA QR — Scan for invoice</div>
            <div dir="rtl" style="font-size:16px;font-family:${AR_FONT}">${esc(AR.scan)}</div>
        </div>` : ""}

        ${promoText ? `${rule()}
        <div style="text-align:center;font-size:19px;font-weight:600">${esc(promoText)}</div>` : ""}

        ${rule()}

        <div style="text-align:center;font-size:21px;margin-top:6px">
            ${settings?.footer_text ? `<div style="margin-bottom:4px">${esc(settings.footer_text)}</div>` : ""}
            <div style="font-weight:700">Thank you</div>
            <div dir="rtl" style="font-weight:700;font-family:${AR_FONT}">${esc(AR.thankYou)}</div>
        </div>
    </div>`;
};

/**
 * A single item line: name (+ Arabic), qty, price.
 * Pass qty/price as "" (or null) to leave that cell blank — used for combo sub-rows
 * that mirror the order view (e.g. the combo add-ons total has no qty; a combo's
 * menu item shows a qty but no price).
 */
function itemRow(nameEn, nameAr, qty, price, indent = 0) {
    const qtyCell = qty === "" || qty === null || qty === undefined ? "" : `x${esc(qty)}`;
    const priceCell = price === "" || price === null || price === undefined ? "" : esc(showPrice(price));
    const pad = indent ? `padding-left:${indent}px;` : "";
    return `
        <tr>
            <td style="padding:4px 0;vertical-align:top;${pad}">
                <div>${esc(nameEn)}</div>
                ${nameAr ? `<div dir="rtl" style="font-family:${AR_FONT};font-size:18px">${esc(nameAr)}</div>` : ""}
            </td>
            <td style="padding:4px 0;text-align:center;white-space:nowrap;vertical-align:top">${qtyCell}</td>
            <td style="padding:4px 0;text-align:right;white-space:nowrap;vertical-align:top;font-family:monospace">${priceCell}</td>
        </tr>`;
}

// On-screen QR size (CSS px). The receipt reserves an empty slot of this size; the
// QR is composited into it afterwards (see stampQr).
const QR_CSS_PX = 340;

/** Load an image src and resolve once it's decoded. */
const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

/**
 * Composite the ZATCA QR directly onto the finished canvas at the reserved slot.
 *
 * Why not an <img> in the receipt? html2canvas is unreliable rendering data-URL
 * images (especially a 2nd one, and outright refuses them when useCORS is on) — the
 * QR kept coming out blank. So we leave an empty slot, then draw the QR ourselves.
 *
 * The QR PNG is generated at EXACTLY the slot's device-pixel size (derived from the
 * real canvas/node ratio, not an assumed scale) and drawn 1:1 with smoothing OFF, so
 * it's a pure pixel copy — razor sharp, fast to scan. Must run while `node` is still
 * in the DOM (for measurement).
 */
const stampQr = async (canvas, node, data) => {
    try {
        const slot = node.querySelector("#qr-slot");
        if (!slot) return;
        const nodeRect = node.getBoundingClientRect();
        const slotRect = slot.getBoundingClientRect();
        const sx = canvas.width / nodeRect.width;
        const sy = canvas.height / nodeRect.height;
        const dx = Math.round((slotRect.left - nodeRect.left) * sx);
        const dy = Math.round((slotRect.top - nodeRect.top) * sy);
        const dw = Math.round(slotRect.width * sx);
        const dh = Math.round(slotRect.height * sy);

        const url = await QRCode.toDataURL(String(data), {
            margin: 4,
            width: dw, // native == draw size → true 1:1, no resample
            errorCorrectionLevel: "L",
            color: { dark: "#000000", light: "#ffffff" },
        });
        const img = await loadImage(url);
        const ctx = canvas.getContext("2d");
        // html2canvas leaves a scale(scale,scale) transform on the context — reset
        // it so we draw in real device pixels, not doubled (which clipped the QR).
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, dx, dy, dw, dh);
    } catch (e) {
        console.log("QR stamp failed ====> ", e);
    }
};

/** Resolve what the QR should encode: ZATCA TLV if present, else an invoice URL. */
const resolveQrData = (order) => {
    const zatca = order?.zatca_details?.qr || order?.transaction?.zatca_details?.qr;
    if (zatca) return zatca;
    const base = process.env.REACT_APP_INVOICE_QR_BASE_URL;
    const id = order?.order_id || order?.id;
    return base && id ? `${base}/${id}` : "";
};

// Load Cairo (Arabic) once so html2canvas captures shaped Arabic glyphs.
let fontInjected = false;
const ensureArabicFont = async () => {
    if (!fontInjected && typeof document !== "undefined") {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap";
        document.head.appendChild(link);
        fontInjected = true;
    }
    try {
        if (document.fonts?.ready) await Promise.race([document.fonts.ready, wait(1500)]);
    } catch (e) {
        /* fonts are best-effort; fall back to system Arabic font */
    }
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch a (possibly cross-origin) image URL and return it as a base64 data URL.
 * Data URLs never taint the canvas, so the logo prints reliably even when the
 * host (e.g. GCP) doesn't send CORS headers for <img>. Falls back to the raw URL.
 */
const urlToDataUrl = async (url) => {
    try {
        const res = await fetch(url, { mode: "cors" });
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
        });
    } catch (e) {
        return url; // let html2canvas try via useCORS as a last resort
    }
};

/**
 * Render an order's customer invoice to an off-screen <canvas> (80mm receipt).
 * @param {object} order     order from GetSingleOrderDetails
 * @param {object} settings  customer-invoice settings from GetCustomerInvoice
 * @param {{scale?:number}} [opts]
 * @returns {Promise<HTMLCanvasElement>}
 */
export const renderReceiptCanvas = async (order = {}, settings = {}, opts = {}) => {
    await ensureArabicFont();

    const scale = Number(opts.scale) || 2; // 2x for crisp text in the PDF

    const qrData = resolveQrData(order);

    // Inline the logo as a data URL so it always renders into the canvas.
    const logoData = settings?.logo ? await urlToDataUrl(settings.logo) : "";
    const settingsForRender = { ...settings, logo: logoData };

    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.left = "-10000px";
    host.style.top = "0";
    host.style.background = "#ffffff";
    host.innerHTML = buildReceiptHtml(order, settingsForRender, !!qrData);
    document.body.appendChild(host);

    const node = host.firstElementChild;

    try {
        await waitForImages(node);
        // NOTE: no useCORS — html2canvas refuses to draw data-URL <img>s with it on.
        // The logo is inlined as a data URL; the QR is composited afterwards.
        const canvas = await html2canvas(node, {
            width: RECEIPT_WIDTH_PX,
            windowWidth: RECEIPT_WIDTH_PX,
            scale,
            backgroundColor: "#ffffff",
            logging: false,
        });

        // Composite the QR crisply onto the slot (node must still be in the DOM).
        if (qrData) await stampQr(canvas, node, qrData);

        return canvas;
    } finally {
        document.body.removeChild(host);
    }
};

/** Render an order's customer invoice to a PNG data URL (an 80mm receipt image). */
export const renderReceiptImage = async (order = {}, settings = {}, opts = {}) => {
    const canvas = await renderReceiptCanvas(order, settings, opts);
    return canvas.toDataURL("image/png");
};

// A4 page in mm and the receipt's printed width (80mm thermal roll).
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const RECEIPT_WIDTH_MM = 80;

/**
 * Build the customer invoice and trigger a browser download of an A4 PDF with the
 * 80mm receipt placed in the centre of the page.
 * @param {object} order     order from GetSingleOrderDetails
 * @param {object} settings  customer-invoice settings from GetCustomerInvoice
 * @param {{scale?:number, fileName?:string}} [opts]
 * @returns {Promise<void>}
 */
export const downloadInvoicePdf = async (order = {}, settings = {}, opts = {}) => {
    const canvas = await renderReceiptCanvas(order, settings, opts);
    return canvasToPdf(canvas, order, opts);
};

/**
 * Turn an already-rendered receipt canvas into an A4 PDF (80mm receipt centred)
 * and trigger a download. Used by the preview modal so it doesn't re-render.
 * @param {HTMLCanvasElement} canvas
 * @param {object} [order]  used only for the file name
 * @param {{fileName?:string}} [opts]
 */
export const canvasToPdf = (canvas, order = {}, opts = {}) => {
    const imgData = canvas.toDataURL("image/png");

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    // Keep the receipt at 80mm wide; shrink to fit A4 height if it's very long.
    let w = RECEIPT_WIDTH_MM;
    let h = (canvas.height * w) / canvas.width;
    const maxH = A4_HEIGHT_MM - 20; // 10mm top/bottom breathing room
    if (h > maxH) {
        const k = maxH / h;
        h *= k;
        w *= k;
    }

    // Centre horizontally and vertically.
    const x = (A4_WIDTH_MM - w) / 2;
    const y = (A4_HEIGHT_MM - h) / 2;
    doc.addImage(imgData, "PNG", x, y, w, h);

    const id = order?.order_id || order?.id || "receipt";
    doc.save(opts.fileName || `invoice-${id}.pdf`);
};

/** Wait for every <img> in the node to finish loading (best-effort, capped). */
const waitForImages = (node) => {
    const imgs = Array.from(node.querySelectorAll("img"));
    return Promise.all(
        imgs.map(
            (img) =>
                new Promise((resolve) => {
                    if (img.complete && img.naturalWidth > 0) return resolve();
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                    setTimeout(resolve, 4000); // never block the PDF on a slow/blocked logo
                })
        )
    );
};

export default { renderReceiptCanvas, renderReceiptImage, downloadInvoicePdf, canvasToPdf, RECEIPT_WIDTH_PX };
